import React, { useEffect, useId, useMemo, useState } from "react";
import {
  CATALOG_MATERIAL_TYPE,
  LABOUR_PRICING_METHOD,
  addWorkTypeLabour,
  deleteWorkTypeLabour,
  filterCatalogByMaterialType,
  updateWorkTypeLabour,
} from "../../../../helpers/estimates";
import { computeAreaBreakdown, getLabourClientRate, hasPriceOverride } from "../../../../helpers/estimateLinePricing";
import { computeLabourLineTotal, computeLabourSqft, formatCurrency } from "../../../../helpers/estimateTotals";
import { filterMaterialsCatalog } from "../../../../helpers/materialSearch";
import { EditableGroup, RowStatus } from "./EditableGroup";
import { RowDelete } from "./RowDelete";
import { CatalogDialog, SurfacePicker, YourPriceField } from "./editorWidgets";

const METHOD_OPTIONS = [
  { value: LABOUR_PRICING_METHOD.HOURLY, label: "Hourly" },
  { value: LABOUR_PRICING_METHOD.SQUARE_FOOT, label: "Per sq ft" },
];

const BLANK = Object.freeze({
  activity: "",
  method: LABOUR_PRICING_METHOD.HOURLY,
  hours: "",
  rate: "",
  overrideMode: "",
  overrideValue: "",
  surfaces: null,
});

function baselineFromRow(row) {
  const surfaces = Array.isArray(row.surface_section_ids) && row.surface_section_ids.length
    ? row.surface_section_ids
    : null;
  return {
    activity: row.labour_activity || "",
    method: row.pricing_method || LABOUR_PRICING_METHOD.HOURLY,
    hours: row.estimated_hours == null ? "" : String(row.estimated_hours),
    rate: row.hourly_rate == null ? "" : String(row.hourly_rate),
    overrideMode: row.price_override_mode || "",
    overrideValue: row.price_override_value == null ? "" : String(row.price_override_value),
    surfaces,
  };
}

function overrideFields(draft) {
  return {
    priceOverrideMode: draft.overrideValue === "" || draft.overrideValue == null ? null : (draft.overrideMode || "amount"),
    priceOverrideValue: draft.overrideValue,
  };
}

/** Draft → the row shape estimateTotals understands (so line totals use existing math). */
function toTotalsRow(draft) {
  return {
    pricing_method: draft.method,
    estimated_hours: draft.method === LABOUR_PRICING_METHOD.HOURLY && draft.hours !== "" ? draft.hours : null,
    hourly_rate: draft.rate === "" ? null : draft.rate,
    price_override_mode: overrideFields(draft).priceOverrideMode,
    price_override_value: draft.overrideValue === "" ? null : draft.overrideValue,
    surface_section_ids: draft.method === LABOUR_PRICING_METHOD.SQUARE_FOOT ? draft.surfaces : null,
  };
}

function ServicePicker({ draft, update, commit, services, disabled, autoFocus }) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const results = useMemo(
    () => (draft.activity.trim() ? filterMaterialsCatalog(services, draft.activity, { limit: 8 }) : services.slice(0, 8)),
    [services, draft.activity],
  );

  function choose(service) {
    const hourly = draft.method !== LABOUR_PRICING_METHOD.SQUARE_FOOT;
    update({
      activity: service.material_name,
      rate: draft.rate === "" ? (service.unit_cost == null ? "" : String(service.unit_cost)) : draft.rate,
      hours: hourly && draft.hours === "" ? "1" : draft.hours,
    });
    setOpen(false);
    commit?.();
  }

  return (
    <div className="eg-combo">
      <input
        className="eg-input"
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Description"
        data-col="desc"
        placeholder="Describe the labour or pick a service…"
        autoComplete="off"
        autoFocus={autoFocus}
        disabled={disabled}
        value={draft.activity}
        onChange={(event) => {
          update({ activity: event.target.value });
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(event) => {
          if (!open || results.length === 0) return;
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((i) => Math.min(i + 1, results.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            choose(results[Math.min(active, results.length - 1)]);
          } else if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
          }
        }}
      />
      {open && results.length > 0 ? (
        <ul id={listId} className="eg-combo__list" role="listbox" aria-label="Services">
          {results.map((service, index) => (
            <li
              key={service.material_id}
              role="option"
              aria-selected={index === active}
              className={index === active ? "is-active" : ""}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(service);
              }}
            >
              <span>{service.material_name}</span>
              <span className="eg-combo__meta">{formatCurrency(service.unit_cost)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LabourRow({ row, workType, services, isNew, disabled, onSave, onDelete, onDraft, onStatus, focusFirst }) {
  const areas = useMemo(() => computeAreaBreakdown(workType), [workType]);
  return (
    <EditableGroup
      value={isNew ? BLANK : baselineFromRow(row)}
      isNew={isNew}
      disabled={disabled}
      onSave={onSave}
      onStatus={onStatus}
      onDraft={onDraft}
      isComplete={isNew ? (d) => Boolean(d.activity.trim()) : undefined}
      testId={`labour-row-${isNew ? "new" : row.labour_id}`}
    >
      {({ draft, update, commit, status }) => {
        const priced = toTotalsRow(draft);
        const sqft = computeLabourSqft(priced, workType);
        const lineTotal = computeLabourLineTotal(priced, workType);
        const perSqft = draft.method === LABOUR_PRICING_METHOD.SQUARE_FOOT;
        const listRate = draft.rate === "" ? 0 : Number(draft.rate) || 0;
        const clientRate = getLabourClientRate(priced);
        return (
          <tr className="eg-row">
            <td>
              {isNew ? (
                <ServicePicker draft={draft} update={update} commit={commit} services={services} disabled={disabled} autoFocus={focusFirst} />
              ) : (
                <input
                  className="eg-input"
                  aria-label="Description"
                  data-col="desc"
                  value={draft.activity}
                  disabled={disabled}
                  onChange={(event) => update({ activity: event.target.value })}
                />
              )}
            </td>
            <td>
              <select
                className="eg-input"
                aria-label="Method"
                data-col="method"
                value={draft.method}
                disabled={disabled}
                onChange={(event) => update({
                  method: event.target.value,
                  hours: event.target.value === LABOUR_PRICING_METHOD.HOURLY ? draft.hours : "",
                })}
              >
                {METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </td>
            <td className="eg-num">
              {perSqft ? (
                <div className="eg-sqft">
                  <SurfacePicker
                    areas={areas}
                    value={draft.surfaces}
                    disabled={disabled}
                    onChange={(surfaces) => update({ surfaces })}
                  />
                  <span className="eg-computed">{sqft.toFixed(1)} ft²</span>
                </div>
              ) : (
                <input className="eg-input eg-input--num" type="number" min="0" step="any" aria-label="Hours"
                  data-col="hours" placeholder="hrs" value={draft.hours} disabled={disabled}
                  onChange={(event) => update({ hours: event.target.value })} />
              )}
            </td>
            <td className="eg-computed">{perSqft ? "ft²" : "hr"}</td>
            <td className="eg-num">
              <input className="eg-input eg-input--num" type="number" min="0" step="any"
                aria-label={perSqft ? "Rate per sq ft" : "Rate per hour"}
                data-col="rate" placeholder="0.00" value={draft.rate} disabled={disabled}
                onChange={(event) => update({ rate: event.target.value })} />
              {hasPriceOverride(priced) ? <span className="eg-mark">Adjusted</span> : null}
              <span className="eg-internal"><span>Charged {formatCurrency(clientRate)}</span></span>
            </td>
            <td>
              <YourPriceField
                mode={draft.overrideMode}
                value={draft.overrideValue}
                unitPrice={listRate}
                disabled={disabled}
                onChange={(next) => update({ overrideMode: next.mode, overrideValue: next.value })}
              />
            </td>
            <td className="eg-num eg-computed" aria-label="Line total">{formatCurrency(lineTotal)}</td>
            <td className="eg-actions"><RowStatus status={status} /></td>
            <td className="eg-delete">
              {!isNew ? (
                <RowDelete label={row.labour_activity || "labour"} disabled={disabled} onConfirm={() => onDelete(row)} />
              ) : null}
            </td>
          </tr>
        );
      }}
    </EditableGroup>
  );
}

/** Labour grid on addWorkTypeLabour / updateWorkTypeLabour / deleteWorkTypeLabour. */
export function LabourTable({ workType, liveWorkType, materialsCatalog, actor, editable, onChanged, onDraft, onStatus }) {
  const [focusToken, setFocusToken] = useState(0);
  const [catalogOpen, setCatalogOpen] = useState(false);
  useEffect(() => {
    if (!focusToken) return undefined;
    document.querySelector('[data-testid="labour-row-new"] input')?.focus();
    return undefined;
  }, [focusToken]);
  const services = useMemo(
    () => filterCatalogByMaterialType(materialsCatalog, CATALOG_MATERIAL_TYPE.SERVICE),
    [materialsCatalog],
  );
  const rows = workType.labour || [];
  const areaSource = liveWorkType || workType;
  const featureId = workType.estimate_feature_id;

  const payload = (draft, notes = "") => ({
    labourActivity: draft.activity,
    estimatedHours: draft.hours,
    hourlyRate: draft.rate,
    notes,
    pricingMethod: draft.method,
    surfaceSectionIds: draft.method === LABOUR_PRICING_METHOD.SQUARE_FOOT ? draft.surfaces : null,
    ...overrideFields(draft),
  });

  return (
    <section className="ed-section" aria-labelledby="ed-labour-h">
      <div className="ed-section__bar">
        <h3 id="ed-labour-h" className="ed-section__title">Labour</h3>
        <button type="button" className="eg-link" onClick={() => setCatalogOpen(true)}>View catalog</button>
      </div>
      <div className="ed-tablewrap">
        <table className="eg-table">
          <thead>
            <tr>
              <th>Description</th>
              <th style={{ width: 118 }}>Method</th>
              <th className="eg-num" style={{ width: 130 }}>Qty</th>
              <th style={{ width: 40 }}>Unit</th>
              <th className="eg-num" style={{ width: 96 }}>Unit price</th>
              <th style={{ width: 128 }}>Your price</th>
              <th className="eg-num" style={{ width: 84 }}>Line total</th>
              <th style={{ width: 72 }} aria-label="Status" />
              <th style={{ width: 36 }} aria-label="Remove" />
            </tr>
          </thead>
          {rows.map((row) => (
              <LabourRow
              key={row.labour_id}
              row={row}
              workType={areaSource}
              services={services}
              disabled={!editable}
              onDraft={(draft) => onDraft(row.labour_id, draft ? toTotalsRow(draft) : null)}
              onSave={async (draft) => {
                await updateWorkTypeLabour(actor, row.labour_id, payload(draft, row.notes || ""));
                await onChanged();
              }}
              onDelete={async () => {
                onStatus("saving");
                try {
                  await deleteWorkTypeLabour(actor, row.labour_id);
                  await onChanged();
                  onStatus("saved");
                } catch (err) {
                  onStatus("error", err.message || "Unable to remove labour.");
                }
              }}
              onStatus={onStatus}
            />
          ))}
          {editable ? (
              <LabourRow
              key={`new-${focusToken}`}
              isNew
              workType={areaSource}
              services={services}
              focusFirst={focusToken > 0}
              onStatus={onStatus}
              onSave={async (draft) => {
                await addWorkTypeLabour(actor, featureId, payload(draft));
                await onChanged();
                setFocusToken((t) => t + 1);
              }}
            />
          ) : null}
        </table>
      </div>
      {editable ? <p className="ed-hint">Pick a service from the list or type a description. Per sq ft lines bill the surfaces you choose. Hourly stays hours × rate. It saves when you leave the row (or press Enter).</p> : null}
      {catalogOpen ? <CatalogDialog title="Service catalog" rows={services} onClose={() => setCatalogOpen(false)} /> : null}
    </section>
  );
}
